/**
 * Fee absorption ("fee calculator") math, shared by the backend (apply/revert)
 * and the frontend (live preview) so the two can never drift.
 *
 * All money is integer cents. All rates are decimal fractions (0.029 === 2.9%).
 *
 * The goal: the builder wants to NET their intended price after Whereabouts
 * and Stripe fees, without adding a separate fee line at checkout. So we scale
 * the sticker price up instead.
 *
 * Fees are charged on the full amount the customer pays (price + tax), while
 * tax is passed through to the tax authority. To net `P` after fees:
 *
 *   P' - rate * P' * (1 + tax) - fixed = P
 *   P' = (P + fixed) / (1 - rate * (1 + tax))
 *
 * and then P' is rounded UP to the builder's chosen increment so prices stay
 * tidy.
 */

export const ROUNDING_INCREMENTS_CENTS = [5, 10, 25, 50, 100] as const;
export type RoundingIncrementCents = (typeof ROUNDING_INCREMENTS_CENTS)[number];

/** Stripe's published card rate: 2.9% + $0.30. Builders can override both. */
export const DEFAULT_STRIPE_PERCENT = 0.029;
export const DEFAULT_STRIPE_FIXED_CENTS = 30;
export const DEFAULT_ROUNDING_INCREMENT_CENTS: RoundingIncrementCents = 100;

export type FeeAbsorptionRates = {
  /** Whereabouts fee, i.e. `paymentMetadata.applicationPercentage`. */
  applicationPercent: number;
  stripePercent: number;
  stripeFixedCents: number;
  /** Combined tax rate on the widget (0 when untaxed). */
  taxPercent: number;
  roundingIncrementCents: number;
};

export function isRoundingIncrementCents(
  value: unknown,
): value is RoundingIncrementCents {
  return (
    typeof value === "number" &&
    (ROUNDING_INCREMENTS_CENTS as readonly number[]).includes(value)
  );
}

/** Fraction of the customer's total (tax included) lost to percentage fees. */
export function effectiveFeeRate(
  rates: Pick<
    FeeAbsorptionRates,
    "applicationPercent" | "stripePercent" | "taxPercent"
  >,
): number {
  return (
    (rates.applicationPercent + rates.stripePercent) * (1 + rates.taxPercent)
  );
}

export function roundUpToIncrement(cents: number, incrementCents: number) {
  if (!(incrementCents > 0)) return Math.ceil(cents);
  // The epsilon stops float noise (e.g. 1000.0000000001) from bumping an
  // already-tidy price up a whole increment.
  return Math.ceil(cents / incrementCents - 1e-9) * incrementCents;
}

/**
 * Suggested sticker price for a price that should net `netCents`.
 * Zero/free prices are returned untouched, and the result is never lower than
 * the input.
 */
export function grossUpPrice(
  netCents: number,
  rates: FeeAbsorptionRates,
  { applyFixedFee }: { applyFixedFee: boolean },
): number {
  if (!Number.isFinite(netCents) || netCents <= 0) return netCents;

  const rate = effectiveFeeRate(rates);
  if (rate < 0 || rate >= 1) {
    throw new Error("Combined fee rate must be between 0% and 100%");
  }

  const fixed = applyFixedFee ? Math.max(0, rates.stripeFixedCents) : 0;
  const raw = (netCents + fixed) / (1 - rate);
  return Math.max(netCents, roundUpToIncrement(raw, rates.roundingIncrementCents));
}

/**
 * The fixed Stripe fee is charged once per order. We fold it into main-product
 * prices only (never add-ons). Accommodation prices are per night, so adding a
 * per-order amount would multiply it by the length of stay; those get the
 * percentage only and the round-up absorbs the remainder.
 */
export function appliesFixedFee({
  productType,
  isMain,
}: {
  productType?: string | null;
  isMain: boolean;
}): boolean {
  return isMain && productType !== "ACCOMMODATION";
}

/** Approximate fees baked into a total, for the optional checkout note. */
export function estimateIncludedFees(
  totalCents: number,
  rates: Pick<
    FeeAbsorptionRates,
    "applicationPercent" | "stripePercent" | "stripeFixedCents"
  >,
): number {
  if (!Number.isFinite(totalCents) || totalCents <= 0) return 0;
  return (
    Math.round(totalCents * (rates.applicationPercent + rates.stripePercent)) +
    Math.max(0, rates.stripeFixedCents)
  );
}

// ---------------------------------------------------------------------------
// Price points
// ---------------------------------------------------------------------------

/**
 * One editable price on a product. `path` is stable across reorders because it
 * is keyed by the sub-document `_id` (falling back to `#<index>`).
 */
export type PricePoint = {
  path: string;
  label: string;
  price: number;
};

type PriceItem = {
  _id?: unknown;
  price?: number | null;
  isActive?: boolean | null;
  name?: unknown;
  label?: unknown;
};

/** Structural subset of a Product (works for mongoose docs and plain objects). */
export type PricedProduct = {
  type?: string | null;
  pricing?: { tiers?: ArrayLike<PriceItem> | null } | null;
  sellingConfig?: {
    basePrice?: number | null;
    options?: ArrayLike<PriceItem> | null;
  } | null;
  productTiers?: ArrayLike<PriceItem> | null;
  merchMetadata?: { variants?: ArrayLike<PriceItem> | null } | null;
};

type Collection = "sellingConfig.options" | "productTiers" | "pricing.tiers" | "merchMetadata.variants";

const COLLECTION_PATH = /^(sellingConfig\.options|productTiers|pricing\.tiers|merchMetadata\.variants)\.(.+)$/;
const BASE_PRICE_PATH = "sellingConfig.basePrice";

function toArray<T>(list: ArrayLike<T> | null | undefined): T[] {
  return list ? Array.from(list) : [];
}

function getCollection(product: PricedProduct, collection: Collection): PriceItem[] {
  switch (collection) {
    case "sellingConfig.options":
      return toArray(product.sellingConfig?.options);
    case "productTiers":
      return toArray(product.productTiers);
    case "pricing.tiers":
      return toArray(product.pricing?.tiers);
    case "merchMetadata.variants":
      return toArray(product.merchMetadata?.variants);
  }
}

function itemKey(item: PriceItem, index: number): string {
  return item._id != null && String(item._id).length > 0
    ? String(item._id)
    : `#${index}`;
}

function pickLabel(item: PriceItem): string {
  const source = item.name ?? item.label;
  if (typeof source === "string") return source;
  if (source && typeof source === "object") {
    const record = source as Record<string, unknown>;
    if (typeof record.en === "string" && record.en) return record.en;
    const first = Object.values(record).find(
      v => typeof v === "string" && v.length > 0,
    );
    if (typeof first === "string") return first;
  }
  return "";
}

const isPositive = (price: unknown): price is number =>
  typeof price === "number" && Number.isFinite(price) && price > 0;

/**
 * Every price the pricing engine can read for this product, mirroring
 * `ProductsService.computePurchasePriceForSingleProduct` so nothing is
 * scaled that wouldn't be charged (and nothing is scaled twice):
 *
 * - Accommodation: `pricing.tiers[]`
 * - Everything else: `sellingConfig.basePrice` and options (legacy
 *   `productTiers` / `pricing.tiers[0]` when there is no selling config), plus
 *   merch variants.
 *
 * Free (<= 0) prices and archived tiers are skipped.
 */
export function collectPricePoints(product: PricedProduct): PricePoint[] {
  const points: PricePoint[] = [];

  if (product.type === "ACCOMMODATION") {
    toArray(product.pricing?.tiers).forEach((tier, i) => {
      if (isPositive(tier.price)) {
        points.push({
          path: `pricing.tiers.${itemKey(tier, i)}`,
          label: pickLabel(tier),
          price: tier.price,
        });
      }
    });
    return points;
  }

  const sellingConfig = product.sellingConfig;
  const optionsCollection: Collection = sellingConfig?.options
    ? "sellingConfig.options"
    : "productTiers";
  const options = getCollection(product, optionsCollection);

  if (sellingConfig && typeof sellingConfig.basePrice === "number") {
    if (isPositive(sellingConfig.basePrice)) {
      points.push({
        path: BASE_PRICE_PATH,
        label: "",
        price: sellingConfig.basePrice,
      });
    }
  } else if (options.length === 0) {
    const legacy = toArray(product.pricing?.tiers)[0];
    if (legacy && isPositive(legacy.price)) {
      points.push({
        path: `pricing.tiers.${itemKey(legacy, 0)}`,
        label: "",
        price: legacy.price,
      });
    }
  }

  options.forEach((option, i) => {
    if (option.isActive === false || !isPositive(option.price)) return;
    points.push({
      path: `${optionsCollection}.${itemKey(option, i)}`,
      label: pickLabel(option),
      price: option.price,
    });
  });

  if (product.type === "MERCH") {
    toArray(product.merchMetadata?.variants).forEach((variant, i) => {
      if (variant.isActive === false || !isPositive(variant.price)) return;
      points.push({
        path: `merchMetadata.variants.${itemKey(variant, i)}`,
        label: pickLabel(variant),
        price: variant.price,
      });
    });
  }

  return points;
}

function locate(
  product: PricedProduct,
  path: string,
): { read: () => number | undefined; write: (price: number) => void } | null {
  if (path === BASE_PRICE_PATH) {
    const sellingConfig = product.sellingConfig;
    if (!sellingConfig) return null;
    return {
      read: () => sellingConfig.basePrice ?? undefined,
      write: price => {
        sellingConfig.basePrice = price;
      },
    };
  }

  const match = COLLECTION_PATH.exec(path);
  if (!match) return null;
  const collection = match[1] as Collection;
  const key = match[2];
  const items = getCollection(product, collection);
  const item = key.startsWith("#")
    ? items[Number(key.slice(1))]
    : items.find((candidate, i) => itemKey(candidate, i) === key);
  if (!item) return null;

  return {
    read: () => item.price ?? undefined,
    write: price => {
      item.price = price;
    },
  };
}

/** Current price at `path`, or undefined if that price point no longer exists. */
export function readPricePoint(
  product: PricedProduct,
  path: string,
): number | undefined {
  return locate(product, path)?.read();
}

/** Writes `price` at `path`. Returns false if the price point can't be found. */
export function writePricePoint(
  product: PricedProduct,
  path: string,
  price: number,
): boolean {
  const target = locate(product, path);
  if (!target) return false;
  target.write(price);
  return true;
}
